import { Box, Container, Typography, Button, Grid } from "@mui/material";
import {
  ArrowForward as ArrowIcon,
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
  FormatQuote as QuoteIcon,
} from "@mui/icons-material";

const SDG_COLORS = [
  "#E5243B","#DDA63A","#4C9F38","#C5192D","#FF3A21","#26BDE2",
  "#FCC30B","#A21942","#FD6925","#DD1367","#FD9D24","#BF8B2E",
  "#3F7E44","#0A97D9","#56C02B","#00689D","#19486A",
];

const impactStats = [
  { num: "4+", label: "Active Programs", accent: "#4C9F38" },
  { num: "54", label: "African Nations", accent: "#26BDE2" },
  { num: "4",  label: "UN SDGs Aligned", accent: "#DDA63A" },
  { num: "∞",  label: "Communities",     accent: "#E5243B" },
];

const achievements = [
  { year: "2026", color: "#DDA63A", title: "Africa Summit for Sustainable Development", sub: "ASSD 2026 — Virtual", body: "BTSA represented at Africa's premier sustainability summit — 7 members presented, connecting with change-makers across the continent." },
  { year: "2026", color: "#4E9E8C", title: "GAIME Conference", sub: "Speke Resort Munyonyo, Kampala", body: "Engaged global innovators in discussions around technology-driven solutions for shelter, infrastructure, and sustainable urban growth." },
  { year: "2021–25", color: "#5C3311", title: "IUEA Best Leader Award", sub: "Founder Lewis Bisaso", body: "Action in Africa scholar and award-winning leader — the driving force behind BTSA's youth-led model of sustainable development." },
];

const testimonial = {
  quote:
    "I got my scholarship through the BTSA Scholarship Chapter — they helped me write a stronger personal statement, sharpen my CV, and apply with confidence. This chapter doesn't just share opportunities, it walks with you until you get one.",
  name: "Diana",
  reviews: "12 reviews at Yelp",
  rating: 5,
  image: "/testimon.png",
};

function SDGStrip() {
  return (
    <Box sx={{ display: "flex", height: "4px" }}>
      {SDG_COLORS.map((c, i) => (
        <Box key={i} sx={{ flex: 1, bgcolor: c }} />
      ))}
    </Box>
  );
}

function TestimonialsSection() {
  return (
    <Box id="testimonials" sx={{ position: "relative", bgcolor: "#FFFFFF", overflow: "hidden" }}>
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Grid container alignItems="center" spacing={{ xs: 6, md: 8 }}>
          {/* Left: copy */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
              <Box sx={{ width: 28, height: 2, bgcolor: "#DDA63A", borderRadius: 1 }} />
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "3px",
                  textTransform: "uppercase",
                  color: "#5C3311",
                }}
              >
                Community Voices
              </Typography>
            </Box>

            <Typography
              sx={{
                fontFamily: "'Playfair Display', serif",
                fontSize: { xs: "36px", md: "52px" },
                fontWeight: 900,
                lineHeight: 1.05,
                color: "#1a0a00",
                mb: 3,
                letterSpacing: "-0.5px",
              }}
            >
              What Africa{" "}
              <Box component="span" sx={{ color: "#DDA63A" }}>
                Says
              </Box>
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: "15px", md: "16px" },
                color: "rgba(26,10,0,0.6)",
                lineHeight: 1.8,
                maxWidth: 420,
                mb: 2.5,
                fontWeight: 400,
              }}
            >
              Shelter Africa has received over 500 positive testimonials from
              families and communities across the continent.
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: "15px", md: "16px" },
                color: "rgba(26,10,0,0.6)",
                lineHeight: 1.8,
                maxWidth: 420,
                mb: 4,
                fontWeight: 400,
              }}
            >
              Families, builders, and local leaders have been transformed by
              our affordable housing programmes.
            </Typography>

            <Box
              component="a"
              href="#contact"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1.25,
                fontSize: "13px",
                fontWeight: 700,
                letterSpacing: "0.5px",
                color: "#5C3311",
                textDecoration: "none",
                borderBottom: "2px solid #DDA63A",
                pb: 0.75,
                transition: "color 0.2s",
                "&:hover": { color: "#DDA63A" },
              }}
            >
              Has Shelter Africa changed your life? Share your story.
            </Box>
          </Grid>

          {/* Right: portrait + overlapping testimonial card */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Box sx={{ position: "relative", width: "100%" }}>
              {/* Portrait — no card, no shadow, sits flush on the page like a cutout */}
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: { xs: 420, sm: 480, md: 520 },
                }}
              >
                <Box
                  component="img"
                  src={testimonial.image}
                  alt={testimonial.name}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "50% 20%",
                  }}
                />

                {/* Decorative nav arrow */}
                <Box
                  sx={{
                    position: "absolute",
                    top: { xs: 16, md: 24 },
                    right: { xs: 16, md: 24 },
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    bgcolor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 24px rgba(26,10,0,0.25)",
                  }}
                >
                  <ArrowIcon sx={{ color: "#5C3311", fontSize: 20 }} />
                </Box>
              </Box>

              {/* Overlapping testimonial card */}
              <Box
                sx={{
                  position: { xs: "relative", sm: "absolute" },
                  left: { sm: "8%" },
                  right: { sm: "-6%" },
                  bottom: { sm: -56 },
                  mt: { xs: -6, sm: 0 },
                  mx: { xs: 2, sm: 0 },
                  bgcolor: "#fff",
                  borderLeft: "4px solid #5C3311",
                  borderRadius: "10px",
                  boxShadow: "0 20px 48px rgba(26,10,0,0.16)",
                  p: { xs: 3, md: 3.5 },
                  maxWidth: { sm: 360, md: 400 },
                }}
              >
                <QuoteIcon sx={{ color: "#DDA63A", fontSize: 22, mb: 1, transform: "scaleX(-1)" }} />

                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "rgba(26,10,0,0.75)",
                    lineHeight: 1.75,
                    mb: 2.5,
                  }}
                >
                  {testimonial.quote}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: "15px",
                      fontWeight: 700,
                      color: "#1a0a00",
                    }}
                  >
                    {testimonial.name}
                  </Typography>

                  <Box sx={{ textAlign: "right" }}>
                    <Box sx={{ display: "flex", gap: 0.25, justifyContent: "flex-end" }}>
                      {Array.from({ length: testimonial.rating }).map((_, i) => (
                        <StarIcon key={i} sx={{ color: "#DDA63A", fontSize: 16 }} />
                      ))}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        color: "rgba(26,10,0,0.4)",
                        mt: 0.25,
                      }}
                    >
                      {testimonial.reviews}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Spacer to clear the absolutely-positioned card on larger screens */}
        <Box sx={{ display: { xs: "none", sm: "block" }, height: { sm: 56, md: 56 } }} />
      </Container>
    </Box>
  );
}

function ImpactSection() {
  return (
    <Box id="impact" sx={{ position: "relative", overflow: "hidden" }}>
      {/* Hero full-bleed */}
      <Box sx={{ position: "relative", minHeight: { xs: 480, md: "78vh" }, display: "flex", alignItems: "center", overflow: "hidden" }}>
        <Box component="img" src="/Panafrica2.png" alt="BTSA Impact" sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top", animation: "kb 14s ease-in-out infinite alternate", "@keyframes kb": { "0%": { transform: "scale(1.0)" }, "100%": { transform: "scale(1.06)" } } }} />
        <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(105deg, rgba(10,5,0,0.82) 0%, rgba(10,5,0,0.65) 50%, rgba(10,5,0,0.25) 100%)" }} />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2, py: { xs: 8, md: 10 } }}>
          <Grid container alignItems="center" spacing={{ xs: 6, md: 8 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                <Box sx={{ width: 28, height: 2, bgcolor: "#DDA63A", borderRadius: 1 }} />
                <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "3px", textTransform: "uppercase", color: "#DDA63A" }}>Our Footprint</Typography>
              </Box>
              <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: "40px", md: "68px" }, fontWeight: 900, lineHeight: 1.0, color: "#fff", mb: 3, letterSpacing: "-0.5px" }}>
                Impact &amp;<br />
                <Box component="span" sx={{ color: "#DDA63A" }}>Achievements</Box>
              </Typography>
              <Typography sx={{ fontSize: { xs: "16px", md: "17px" }, color: "rgba(255,255,255,0.65)", lineHeight: 1.8, maxWidth: 460, mb: 4, fontWeight: 300 }}>Building the Africa We Want — one community, one shelter, one life at a time.</Typography>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.5, bgcolor: "rgba(221,166,58,0.12)", border: "1px solid rgba(221,166,58,0.4)", borderRadius: "8px", px: 2.5, py: 1.5, mb: 5 }}>
                <TrophyIcon sx={{ color: "#DDA63A", fontSize: 18 }} />
                <Typography sx={{ color: "rgba(255,255,255,0.9)", fontSize: "13px", fontWeight: 600 }}>ASSD 2026 · Certificate of Participation</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button variant="contained" endIcon={<ArrowIcon />} href="#programs" sx={{ bgcolor: "#5C3311", color: "#fff", fontWeight: 700, fontSize: "12px", letterSpacing: "1.5px", textTransform: "uppercase", px: 3.5, py: 1.4, borderRadius: "6px", boxShadow: "0 4px 20px rgba(92,51,17,0.45)", "&:hover": { bgcolor: "#7a4520" } }}>Our Programs</Button>
                <Button variant="outlined" href="#contact" sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.35)", fontWeight: 700, fontSize: "12px", letterSpacing: "1.5px", textTransform: "uppercase", px: 3.5, py: 1.4, borderRadius: "6px", "&:hover": { borderColor: "#DDA63A", color: "#DDA63A", bgcolor: "transparent" } }}>Get Involved</Button>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2 }}>
                {impactStats.map((s) => (
                  <Box key={s.label} sx={{ bgcolor: "rgba(255,255,255,0.06)", backdropFilter: "blur(16px)", border: "1px solid rgba(255,255,255,0.1)", borderTop: `3px solid ${s.accent}`, borderRadius: "10px", p: { xs: 3, md: 3.5 }, textAlign: "center", transition: "all 0.3s ease", "&:hover": { bgcolor: "rgba(255,255,255,0.1)", transform: "translateY(-4px)", boxShadow: "0 20px 48px rgba(0,0,0,0.4)" } }}>
                    <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: "44px", md: "54px" }, fontWeight: 900, color: "#fff", lineHeight: 1, mb: 0.5 }}>{s.num}</Typography>
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: s.accent, textTransform: "uppercase", letterSpacing: "1.5px" }}>{s.label}</Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ mt: 2, bgcolor: "rgba(255,255,255,0.06)", backdropFilter: "blur(16px)", border: "1px solid rgba(255,255,255,0.1)", borderLeft: "4px solid #DDA63A", borderRadius: "10px", px: 3, py: 2.5 }}>
                <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "16px", fontWeight: 700, color: "#fff", fontStyle: "italic", lineHeight: 1.5, mb: 1 }}>"African youth are not waiting. We are building."</Typography>
                <Typography sx={{ fontSize: "11px", color: "#DDA63A", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase" }}>— BTSA Founding Principle</Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Achievements */}
      <Box sx={{ bgcolor: "#FFFFFF", py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: { xs: 5, md: 8 } }}>
            <Typography sx={{ fontSize: { xs: "13px", md: "15px" }, color: "#5C3311", fontWeight: 700 }}>→</Typography>
            <Typography sx={{ fontSize: { xs: "14px", md: "16px" }, color: "#1a0a00", fontWeight: 400 }}>
              Milestones &amp;{" "}
              <Box component="span" sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>Key Achievements</Box>
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {achievements.map((item, i) => (
              <Box key={item.title} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "100px 1fr" }, gap: { xs: 1, md: 4 }, py: { xs: 4, md: 5 }, borderBottom: i < achievements.length - 1 ? "1px solid rgba(26,10,0,0.06)" : "none", px: { xs: 0, md: 2 }, borderRadius: "8px", transition: "background 0.2s", "&:hover": { bgcolor: "#FAFAFA" } }}>
                <Box sx={{ pt: { md: 0.5 } }}>
                  <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "13px", fontWeight: 700, color: item.color, borderLeft: `3px solid ${item.color}`, pl: 1.5, lineHeight: 1.2 }}>{item.year}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "11px", fontWeight: 700, color: item.color, textTransform: "uppercase", letterSpacing: "1.5px", mb: 0.75 }}>{item.sub}</Typography>
                  <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: "20px", md: "24px" }, fontWeight: 700, color: "#1a0a00", lineHeight: 1.25, mb: 1.25 }}>{item.title}</Typography>
                  <Typography sx={{ fontSize: "14px", color: "rgba(26,10,0,0.55)", lineHeight: 1.75, maxWidth: 620 }}>{item.body}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: { xs: 6, md: 8 }, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
            <Typography sx={{ fontSize: "13px", color: "rgba(26,10,0,0.4)", fontStyle: "italic" }}>All achievements align with the UN Sustainable Development Goals &amp; Agenda 2063</Typography>
            <Typography component="a" href="#contact" sx={{ fontSize: "13px", fontWeight: 700, color: "#5C3311", textDecoration: "none", transition: "all 0.2s", "&:hover": { color: "#DDA63A" } }}>Partner with us →</Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default function ImpactWithTestimonials() {
  return (
    <Box>
      <TestimonialsSection />
      <SDGStrip />
      <ImpactSection />
    </Box>
  );
}

export { TestimonialsSection, ImpactSection };